import { FormattedMessage } from 'react-intl';
import { IconButton, Theme } from '@mui/material';
import { PaloColumn } from '@synergy/components-palo';
import { PoolDropzone, PoolLinearProgress, PoolTypography } from '@synergy/components';
import { downloadUrl } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import urljoin from 'url-join';

import * as SupplierDetailDocumentsActions from './store/supplier-detail-documents-actions';
import * as SupplierDetailDocumentsSelectors from './store/supplier-detail-documents-selectors';
import { DocumentsTable } from '../../../components/documents/documents-table';
import { SupplierDetailDocumentsEditTitleModal } from './components/supplier-detail-documents-edit-title-modal';
import { SupplierDocument } from './models/supplier-detail-document';
import { VisibilityIcon } from '../../../../configuration/components/visibility-icon';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    progress: {
        marginTop: theme.spacing(2),
    },
    progressPlaceholder: {
        height: 4,
    },
    dropzone: {
        height: 160,
    },
    sectionContainer: {
        marginTop: theme.spacing(6),
    },
}));

export const SupplierDetailDocuments = () => {
    const dispatch = useThunkDispatch();
    const { id: supplierIdParam } = useParams<{ id: string }>();
    const classes = useStyles();
    const [selectedDocument, setSelectedDocument] = useState<SupplierDocument | null>(null);

    const documents = useAppSelector(SupplierDetailDocumentsSelectors.documentsSelector);
    const progress = useAppSelector(SupplierDetailDocumentsSelectors.overallUploadProgressSelector);
    const documentBaseUrl = useAppSelector(state => state.settings.privateFilesystemPath);

    useEffect(() => {
        if (supplierIdParam && +supplierIdParam) {
            dispatch(SupplierDetailDocumentsActions.fetchSupplierDocuments(+supplierIdParam));
        }
    }, [dispatch, supplierIdParam]);

    const handleEditTitleStart = (document: SupplierDocument) => {
        setSelectedDocument(document);
    };

    const handleEditTitleComplete = (name: string) => {
        if (!selectedDocument) {
            return;
        }
        dispatch(SupplierDetailDocumentsActions.renameDocument(selectedDocument.id, name));
        setSelectedDocument(null);
    };

    const handleDownload = (url: string, documentName: string) => {
        downloadUrl(urljoin(documentBaseUrl, url), documentName);
    };

    const handleDelete = (document: SupplierDocument) => {
        dispatch(SupplierDetailDocumentsActions.deleteDocument(document.id));
    };

    const handleShowInShop = (document: SupplierDocument) => {
        dispatch(SupplierDetailDocumentsActions.setShopVisibility(document.id, !document.isVisibleOnProductPages));
    };

    const handleShowInSupplierDirectory = (document: SupplierDocument) => {
        dispatch(
            SupplierDetailDocumentsActions.setSupplierDirectoryVisibility(
                document.id,
                !document.isVisibleInSupplierDirectory
            )
        );
    };

    // Where a supplier's document is shown besides in the mask itself.
    const visibilityColumns: PaloColumn<SupplierDocument>[] = [
        {
            key: 'isVisibleInSupplierDirectory',
            width: '1%',
            align: 'center',
            head: <FormattedMessage id="app.label.isVisibleInSupplierDirectory" />,
            cell: document => (
                <IconButton onClick={() => handleShowInSupplierDirectory(document)}>
                    <VisibilityIcon isVisible={document.isVisibleInSupplierDirectory} />
                </IconButton>
            ),
        },
        {
            key: 'isVisibleOnProductPages',
            width: '1%',
            align: 'center',
            head: <FormattedMessage id="app.label.isVisibleOnProductPages" />,
            cell: document => (
                <IconButton onClick={() => handleShowInShop(document)}>
                    <VisibilityIcon isVisible={document.isVisibleOnProductPages} />
                </IconButton>
            ),
        },
    ];

    const handleDrop = (acceptedFiles: File[]) => {
        const deduplicatedDocuments = acceptedFiles.filter(
            file => !documents.some(doc => doc.originalName === file.name && doc.size === file.size)
        );
        if (deduplicatedDocuments.length > 0) {
            dispatch(SupplierDetailDocumentsActions.addSupplierDocuments(deduplicatedDocuments));
        }
    };

    return (
        <div>
            <PoolTypography styleType="sectionTitle" id="app.contactsAndOrganizations.documents.upload" />
            <PoolDropzone onDrop={handleDrop} className={classes.dropzone} />
            {progress ? (
                <PoolLinearProgress variant="determinate" value={progress} className={classes.progress} />
            ) : (
                <div className={`${classes.progress} ${classes.progressPlaceholder}`} />
            )}
            {documents && documents.length > 0 && (
                <div className={classes.sectionContainer}>
                    <PoolTypography styleType="sectionTitle" id="app.contactsAndOrganizations.documents.files" />
                    <DocumentsTable
                        documents={documents}
                        downloadFrom="title"
                        extraColumns={visibilityColumns}
                        onDelete={handleDelete}
                        onDownload={handleDownload}
                        onEditTitle={handleEditTitleStart}
                    />
                </div>
            )}
            <SupplierDetailDocumentsEditTitleModal
                document={selectedDocument}
                onClose={() => setSelectedDocument(null)}
                onConfirm={handleEditTitleComplete}
            />
        </div>
    );
};
